import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { createAutomation, updateAutomation, fetchAutomationOptions, fetchAutomations } from '../../redux/Slices/automationSlice';
import { fetchBoardById } from '../../redux/Slices/boardSlice';
import { fetchMembers } from '../../redux/Slices/memberSlice';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import TriggerSelector, { scheduleConfigToCron } from './TriggerSelector';
import ConditionBuilder from './ConditionBuilder';
import ActionSelector from './ActionSelector';
import './Automation.scss';

const TRIGGER_LABELS = {
  card_created: 'When a card is created',
  card_moved_to_list: 'When a card is moved to a list',
  card_moved_from_list: 'When a card is moved from a list',
  due_date_approaching: 'When due date is approaching',
  due_date_passed: 'When due date has passed',
  checklist_completed: 'When all checklist items completed',
  card_completed: 'When a card is marked complete',
  label_added: 'When a label is added',
  priority_changed: 'When priority is changed',
  schedule: 'On a schedule'
};

const ACTION_LABELS = {
  assign_member: 'Assign member',
  unassign_member: 'Unassign member',
  move_to_list: 'Move to list',
  set_due_date: 'Set due date',
  mark_complete: 'Mark complete',
  mark_incomplete: 'Mark incomplete',
  set_priority: 'Set priority',
  add_label: 'Add label',
  archive_card: 'Archive card',
  add_comment: 'Add comment',
  create_card: 'Create card',
  send_notification: 'Send notification'
};

const AutomationRuleBuilder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { boardId, automationId } = useParams();
  const mode = automationId ? 'edit' : 'create';

  const { options, optionsLoading, automations } = useSelector((state) => state.automations);
  const { board } = useSelector((state) => state.board);
  const { boardMembers } = useSelector((state) => state.member);

  const [name, setName] = useState('');
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [triggerConfig, setTriggerConfig] = useState({});
  const [conditions, setConditions] = useState([]);
  const [actions, setActions] = useState([{ type: '', config: {} }]);
  const [saving, setSaving] = useState(false);

  // Load board, options, and members
  useEffect(() => {
    if (boardId) {
      if (!board || board.shortId !== boardId) {
        dispatch(fetchBoardById(boardId));
      }
      if (!options) {
        dispatch(fetchAutomationOptions(boardId));
      }
      if (!boardMembers || boardMembers.length === 0) {
        dispatch(fetchMembers({ type: 'board', id: boardId }));
      }
      if (mode === 'edit' && automations.length === 0) {
        dispatch(fetchAutomations(boardId));
      }
    }
  }, [boardId, dispatch]);

  // Populate form for edit mode
  useEffect(() => {
    if (mode === 'edit' && automationId && automations.length > 0) {
      const automation = automations.find(a => a._id === automationId);
      if (automation) {
        setName(automation.name || '');
        setSelectedTrigger(automation.trigger ? { type: automation.trigger.type } : null);
        setTriggerConfig(automation.trigger?.config || {});
        setConditions(automation.conditions || []);
        setActions(
          automation.actions?.length > 0
            ? automation.actions.map(a => ({ ...a, config: a.config || {} }))
            : [{ type: '', config: {} }]
        );
      }
    }
  }, [mode, automationId, automations]);

  // Auto-generate name when trigger or first action changes
  useEffect(() => {
    if (mode === 'edit') return;
    if (selectedTrigger?.type && actions[0]?.type) {
      const triggerLabel = TRIGGER_LABELS[selectedTrigger.type] || selectedTrigger.type;
      const actionLabel = ACTION_LABELS[actions[0].type] || actions[0].type;
      setName(`${triggerLabel} → ${actionLabel}`);
    }
  }, [selectedTrigger?.type, actions, mode]);

  const handleTriggerSelect = (trigger) => {
    setSelectedTrigger(trigger);
    setTriggerConfig({});
  };

  const handleTriggerConfigChange = (key, value) => {
    setTriggerConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleBack = () => {
    navigate(`/b/${boardId}/automations`);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      dispatch(showSnackBar({ message: 'Please enter a name for the automation', type: 'error' }));
      return;
    }
    if (!selectedTrigger?.type) {
      dispatch(showSnackBar({ message: 'Please select a trigger', type: 'error' }));
      return;
    }
    const validActions = actions.filter(a => a.type);
    if (validActions.length === 0) {
      dispatch(showSnackBar({ message: 'Please add at least one action', type: 'error' }));
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: name.trim(),
        trigger: {
          type: selectedTrigger.type,
          config: triggerConfig
        },
        conditions: conditions.filter(c => c.field && c.operator),
        actions: validActions
      };

      if (selectedTrigger.type === 'schedule') {
        payload.schedule = {
          cronExpression: scheduleConfigToCron(triggerConfig),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
        };
      }

      if (mode === 'edit' && automationId) {
        await dispatch(updateAutomation({
          boardId,
          automationId,
          data: payload
        })).unwrap();
        dispatch(showSnackBar({ message: 'Automation updated', type: 'success' }));
      } else {
        await dispatch(createAutomation({ boardId, data: payload })).unwrap();
        dispatch(showSnackBar({ message: 'Automation created', type: 'success' }));
      }

      navigate(`/b/${boardId}/automations`);
    } catch (error) {
      dispatch(showSnackBar({ message: error.message || 'Failed to save automation', type: 'error' }));
    } finally {
      setSaving(false);
    }
  };

  const lists = board?.actionLists || [];
  const members = boardMembers || [];

  if (optionsLoading) {
    return (
      <div className="automation-page">
        <div className="automation-rule-builder">
          <div className="builder-header">
            <h2>{mode === 'edit' ? 'Edit' : 'Create'} Automation</h2>
          </div>
          <p style={{ textAlign: 'center', padding: '24px', color: 'var(--secondary-text-color)' }}>Loading options...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="automation-page">
      <div className="automation-rule-builder">
        <div className="builder-header">
          <button className="back-btn" onClick={handleBack}>
            <span className="material-symbols-outlined">arrow_back</span>
          </button>
          <h2>{mode === 'edit' ? 'Edit' : 'Create'} Automation</h2>
        </div>

        <div className="builder-body">
          {/* Name */}
          <div className="builder-section">
            <div className="section-label">Name</div>
            <input
              className="builder-name-input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Automation name..."
            />
          </div>

          {/* Trigger */}
          <div className="builder-section">
            <div className="section-label">When (Trigger)</div>
            <TriggerSelector
              triggers={options?.triggers}
              selectedTrigger={selectedTrigger}
              onSelect={handleTriggerSelect}
              onConfigChange={handleTriggerConfigChange}
              triggerConfig={triggerConfig}
              lists={lists}
              members={members}
            />
          </div>

          {/* Conditions */}
          <div className="builder-section">
            <div className="section-label">Only if (Conditions - optional)</div>
            <ConditionBuilder
              conditions={conditions}
              conditionFields={options?.conditionFields}
              operators={options?.operators}
              onChange={setConditions}
              lists={lists}
              members={members}
            />
          </div>

          {/* Actions */}
          <div className="builder-section">
            <div className="section-label">Then do (Actions)</div>
            <ActionSelector
              actions={actions}
              actionOptions={options?.actions}
              onActionsChange={setActions}
              lists={lists}
              members={members}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="builder-footer">
          <button className="cancel-btn" onClick={handleBack}>Cancel</button>
          <button className="save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : (mode === 'edit' ? 'Update' : 'Create Rule')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutomationRuleBuilder;
